import { Settings, Briefcase, Users, MessageCircle, ClipboardList, FileText } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Combined top header + primary tab bar.
 *
 * Previously the 5 primary tabs lived in a `position: fixed` BottomNav
 * portaled into `document.body`. iOS Safari kept detaching that fixed
 * layer to a transformed ancestor and floating the bar mid-page even
 * after multiple containment-block cleanups. The reliable escape is
 * to drop the fixed-bottom pattern entirely and nest the tabs inside
 * the already-stable top sticky header — one stacking context, no
 * portal, no `position: fixed` on the nav itself.
 */
export default function Header({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  // Fetch unread message count on an interval. Same cadence as the
  // old BottomNav so we don't accidentally hammer the API after this
  // refactor.
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem("session_token");
        if (!token) return;
        const response = await axios.get(`${API}/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUnreadCount(response.data.count || 0);
      } catch (error) {
        // Non-fatal — messages module may not be set up yet.
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Per-tab palette — kept from the previous BottomNav so the active
  // state still has visual rhythm. Muted at rest, vivid when active.
  const tabs = [
    { path: "/jobs", label: "Jobs", Icon: Briefcase, testId: "nav-jobs", hue: "text-[#207024]", bg: "bg-white" },
    { path: "/customers", label: "Customers", Icon: Users, testId: "nav-customers", hue: "text-indigo-600", bg: "bg-white" },
    { path: "/messages", label: "Messages", Icon: MessageCircle, testId: "nav-messages", hue: "text-rose-600", bg: "bg-white", badge: true },
    { path: "/estimates", label: "Estimates", Icon: ClipboardList, testId: "nav-estimates", hue: "text-amber-600", bg: "bg-white" },
    { path: "/invoices", label: "Invoices", Icon: FileText, testId: "nav-invoices", hue: "text-emerald-600", bg: "bg-white" },
  ];

  return (
    <div
      className="bg-gradient-to-r from-[#207024] via-[#2a8a2e] to-[#207024] border-b-2 border-[#185a1c] sticky top-0 z-50 pt-[env(safe-area-inset-top)] shadow-sm"
      data-testid="header"
    >
      {/* Row 1 — brand + settings */}
      <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_window-screen-mgmt/artifacts/xq82sr3z_ScreenzZ-1024x1024.png"
            alt="ScreenzZ"
            className="h-10 w-10 rounded-lg bg-white p-0.5 shadow-sm"
            data-testid="header-logo"
          />
          <div>
            <h2 className="font-barlow text-xl font-bold text-white uppercase leading-none tracking-wide">
              ScreenzZ
            </h2>
            {user && (
              <p className="text-xs text-white/80 font-inter" data-testid="user-role">
                {user.name} • {user.role}
              </p>
            )}
          </div>
        </div>
        {user && (
          <button
            onClick={() => navigate("/settings")}
            data-testid="settings-button"
            className="h-9 px-3 rounded-lg bg-white/15 hover:bg-white/25 text-white font-semibold text-sm flex items-center gap-2 backdrop-blur-sm border border-white/20"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        )}
      </div>

      {/* Row 2 — primary tab bar. Only rendered when the user is signed
          in so the login screen stays clean. */}
      {user && (
        <nav
          className="max-w-md mx-auto flex items-center justify-around bg-white border-t border-[#185a1c]/20"
          data-testid="top-tab-bar"
        >
          {tabs.map(({ path, label, Icon, testId, hue, badge }) => {
            const active = isActive(path);
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                data-testid={testId}
                className="flex flex-col items-center gap-0.5 py-2 px-2 flex-1 relative"
              >
                <div className="relative">
                  <Icon
                    className={`w-5 h-5 transition-colors ${
                      active ? hue : "text-zinc-400"
                    }`}
                  />
                  {badge && unreadCount > 0 && (
                    <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[10px] leading-none rounded-full w-4 h-4 flex items-center justify-center font-bold">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </div>
                <span
                  className={`text-[10px] font-semibold transition-colors ${
                    active ? hue : "text-zinc-500"
                  }`}
                >
                  {label}
                </span>
                {active && (
                  <span
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[3px] w-8 rounded-full ${hue.replace(
                      "text-",
                      "bg-"
                    )}`}
                  />
                )}
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}
